import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Snackbar, Alert } from '@mui/material';

const NotificationContext = createContext(null);

const DURATIONS = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: null,
};

export function NotificationProvider({ children }) {
  const [state, setState] = useState({ open: false, message: '', severity: 'info' });

  const close = useCallback((_event, reason) => {
    if (reason === 'clickaway') return;
    setState((s) => ({ ...s, open: false }));
  }, []);

  const dismiss = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  const notify = useMemo(() => {
    const fire = (severity) => (message) => setState({ open: true, message, severity });
    return {
      success: fire('success'),
      error: fire('error'),
      warning: fire('warning'),
      info: fire('info'),
      // Programmatically close the current toast — e.g. once the user resolves the
      // problem that raised a sticky error toast, so it doesn't linger stale.
      dismiss,
    };
  }, [dismiss]);

  return (
    <NotificationContext.Provider value={notify}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={DURATIONS[state.severity]}
        onClose={close}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: { xs: 72, sm: 80 },
          // Offset so the toast is centred over the main content area, not the full
          // viewport (the 240px sidebar should be excluded on sm+).
          left: { xs: '50%', sm: 'calc(50% + 120px)' },
          transform: 'translateX(-50%)',
          zIndex: (theme) => theme.zIndex.snackbar,
        }}
      >
        <Alert
          severity={state.severity}
          onClose={dismiss}
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotification must be used within NotificationProvider');
  return ctx;
}
